import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormArray, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator, ValidatorFn, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, pairwise } from 'rxjs/operators';
import moment from 'moment';
import { isEqual } from 'lodash-es';
import { FieldOperators } from './configs';
import { FieldTypeEnum, OperatorDisplayName, OperatorEnum } from '../../../enums';
import { HubspotEnrichmentFieldQueryFilterItem, HubspotMappingFieldsByItemType } from '../../../dto';
import { NumberWithCommaDirectiveModule } from '../../../directives';
import { TypeaheadDropdownModule } from '../typeahead-dropdown/typeahead-dropdown.module';


@UntilDestroy()
@Component({
  selector: 'app-field-query-filter',
  templateUrl: './field-query-filter.component.html',
  styleUrls: ['./field-query-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldQueryFilterComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FieldQueryFilterComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TypeaheadDropdownModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatButtonModule,
    NumberWithCommaDirectiveModule
  ],
  standalone: true
})
export class FieldQueryFilterComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() set fields(value: HubspotMappingFieldsByItemType[]) {
    this.innerFields = value;

    if (value) {
      this.fieldDataByFieldName = value.reduce((acc: Record<string, HubspotMappingFieldsByItemType>, item: HubspotMappingFieldsByItemType) => ({
        ...acc,
        [item.name]: item,
      }), {})
    }
  }
  get fields() {
    return this.innerFields;
  }
  @Input() isReadonly: boolean;

  form: FormGroup;
  fieldDataByFieldName: Record<string, HubspotMappingFieldsByItemType> = {};

  readonly fieldTypeEnum: typeof FieldTypeEnum = FieldTypeEnum;
  readonly operatorEnum: typeof OperatorEnum = OperatorEnum;
  readonly operators: typeof FieldOperators = FieldOperators;
  readonly operatorDisplayName: typeof OperatorDisplayName = OperatorDisplayName;

  private subscriptions: Subscription[] = [];
  private innerFields: HubspotMappingFieldsByItemType[];
  private onChanged: (value: HubspotEnrichmentFieldQueryFilterItem[]) => void;
  private onTouched: () => void;

  constructor(
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { fieldQueryFilter: { valid: false } };
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      filters: this.fb.array([])
    });
    this.onAddFilter(false);

    this.form.valueChanges.pipe(
      untilDestroyed(this),
    )
    .subscribe(() => {
      const value: { filters: HubspotEnrichmentFieldQueryFilterItem[] } = this.form.getRawValue();

      if (this.onChanged) {
        this.onChanged(value.filters.map((item: HubspotEnrichmentFieldQueryFilterItem & { id: string }) => this.convertValueItem(item)));
      }

      if (this.onTouched) {
        this.onTouched();
      }
    })
  }

  get filters(): FormArray {
    return this.form.get('filters') as FormArray;
  }

  onAddFilter(emitEvent: boolean = true, value?: HubspotEnrichmentFieldQueryFilterItem): void {
    if (this.filters.length < 3) {
      const newFilter = this.fb.group({
        id: this.generateUniqueId(), // Assign a unique ID
        dataType: [value?.dataType],
        operator: [value?.operator, Validators.required],
        operatorDisplayName: [value?.operatorDisplayName],
        field: [value?.field, Validators.required],
        value: [value?.value, value?.value ? [Validators.required, Validators.pattern(/^(?!\s*$).+/)] : [Validators.pattern(/^(?!\s*$).+/)]],
        values: [value?.values, value?.values ? [Validators.required, Validators.pattern(/^(?!\s*$).+/)] : [Validators.pattern(/^(?!\s*$).+/)]],
        range: this.fb.group({
          lowValue: [value?.range?.lowValue, value?.range?.lowValue ? [Validators.required, Validators.pattern(/^(?!\s*$).+/)] : [Validators.pattern(/^(?!\s*$).+/)]],
          highValue: [value?.range?.highValue, value?.range?.highValue ? [Validators.required, Validators.pattern(/^(?!\s*$).+/)] : [Validators.pattern(/^(?!\s*$).+/)]],
        }, { validators: this.rangeValidator })
      });

      this.filters.push(newFilter, { emitEvent });

      this.subscriptions.push(
        newFilter.valueChanges.pipe(
          distinctUntilChanged(isEqual),
          pairwise(),
          untilDestroyed(this)
        // @ts-ignore
        ).subscribe(([oldValue, currValue]: HubspotEnrichmentFieldQueryFilterItem[]) => {
          const filterId: string = newFilter.get('id')?.value; // Get the unique ID
          this.handleFormChanges([oldValue, currValue], filterId);
        })
      );

      this.filters.updateValueAndValidity({ emitEvent });
    }
  }

  onRemoveFilter(index: number, emitEvent: boolean = true): void {
    const removedId = this.filters.at(index).get('id')?.value;

    this.filters.removeAt(index, { emitEvent });

    // Find the subscription related to the removed ID and unsubscribe
    const subIndex = this.subscriptions.findIndex((sub, i) =>
      this.filters.at(i)?.get('id')?.value === removedId
    );

    if (subIndex !== -1) {
      this.subscriptions[subIndex]?.unsubscribe();
      this.subscriptions.splice(subIndex, 1);
    }

    this.filters.updateValueAndValidity({ emitEvent });
  }

  onFieldsChanged(index: number, item: HubspotMappingFieldsByItemType): void {
    this.filters.at(index).patchValue({
      field: item?.name,
      dataType: item?.dataType,
      ...(!item ? { operator: null } : {}),
    });
  }

  writeValue(value: HubspotEnrichmentFieldQueryFilterItem[]): void {
    if (!value) {
      this.onResetFilter(false);
      return;
    }

    this.onResetFilter(false, false);

    value.forEach((item: HubspotEnrichmentFieldQueryFilterItem) => {
      this.onAddFilter(false, item);
    });

    this.form.updateValueAndValidity({ emitEvent: false });
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: HubspotEnrichmentFieldQueryFilterItem[]) => void): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private onResetFilter(emitEvent: boolean = true, addEmpty: boolean = true): void {
    while (this.filters.length !== 0) {
      this.filters.removeAt(0, { emitEvent });
      this.subscriptions[0]?.unsubscribe();
      this.subscriptions.splice(0, 1);
    }
    if (addEmpty) {
      this.onAddFilter(emitEvent);
    }
  }

  private handleFormChanges([oldValue, { dataType, operator }]: HubspotEnrichmentFieldQueryFilterItem[], filterId: string): void {
    const filterIndex = this.filters.controls.findIndex(control => control.get('id')?.value === filterId);

    if (filterIndex === -1) return; // If the form group was removed, ignore

    const filterControl = this.filters.at(filterIndex);

    const checksProperty = [OperatorEnum.IS_NOT_EMPTY, OperatorEnum.IS_EMPTY].includes(operator);

    const valueControl = filterControl.get('value');
    const valuesControl = filterControl.get('values');
    const rangeControl = filterControl.get('range');
    const rangeLowValueControl = filterControl.get('range.lowValue');
    const rangeHighValueControl = filterControl.get('range.highValue');

    if ((dataType !== FieldTypeEnum.BOOLEAN) && checksProperty) {
      this.clearFilterFields(['value', 'values', 'range.lowValue', 'range.highValue'], filterIndex);
      this.removeRequiredValidator(['value', 'values', 'range.lowValue', 'range.highValue'], filterIndex);

      valueControl.disable({ emitEvent: false });
      valuesControl.disable({ emitEvent: false });
      rangeControl.disable({ emitEvent: false });
    } else {
      valueControl.enable({ emitEvent: false });
      valuesControl.enable({ emitEvent: false });
      rangeControl.enable({ emitEvent: false });

      switch (dataType) {
        case FieldTypeEnum.ENUM:
          if (oldValue.dataType !== dataType) {
            this.clearFilterFields(['value', 'range.lowValue', 'range.highValue'], filterIndex);
          }
          this.addRequiredValidator(['values'], filterIndex);
          this.removeRequiredValidator(['value', 'range.lowValue', 'range.highValue'], filterIndex);
          break;
        case FieldTypeEnum.DATE:
        case FieldTypeEnum.DATETIME:
        case FieldTypeEnum.NUMBER:
          this.removeRequiredValidator(['values'], filterIndex);

          if (oldValue.operator !== operator && (oldValue.operator === OperatorEnum.BETWEEN || operator === OperatorEnum.BETWEEN)) {
            this.clearFilterFields(['value', 'range.lowValue', 'range.highValue'], filterIndex);
          }

          if (operator === OperatorEnum.BETWEEN) {
            if (oldValue.dataType !== dataType) {
              this.clearFilterFields(['value', 'values'], filterIndex);
            }
            this.addRequiredValidator(['range.lowValue', 'range.highValue'], filterIndex);
            this.removeRequiredValidator(['value'], filterIndex);
          } else {
            if (oldValue.dataType !== dataType) {
              this.clearFilterFields(['values', 'range.lowValue', 'range.highValue'], filterIndex);
            }
            this.addRequiredValidator(['value'], filterIndex);
            this.removeRequiredValidator(['range.lowValue', 'range.highValue'], filterIndex);
          }
          break;
        default:
          if (oldValue.dataType !== dataType) {
            this.clearFilterFields(['values', 'range.lowValue', 'range.highValue'], filterIndex);
          }
          this.addRequiredValidator(['value'], filterIndex);
          this.removeRequiredValidator(['values', 'range.lowValue', 'range.highValue'], filterIndex);
      }
    }

    valueControl.updateValueAndValidity();
    valuesControl.updateValueAndValidity();
    rangeLowValueControl.updateValueAndValidity();
    rangeHighValueControl.updateValueAndValidity();
  }

  private rangeValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const lowValueControl = group.get('lowValue');
    const highValueControl = group.get('highValue');

    const lowValue = lowValueControl?.value;
    const highValue = highValueControl?.value;

    // Reset previous errors to avoid accumulating them
    lowValueControl?.setErrors(null);
    highValueControl?.setErrors(null);

    if (lowValue !== null && highValue !== null) {
      if (lowValue >= highValue) {
        lowValueControl?.setErrors({ lowTooHigh: 'Low value must be less than high value' });
        highValueControl?.setErrors({ highTooLow: 'High value must be greater than low value' });
        return { rangeInvalid: true }; // Also set a group-level error if needed
      }
    }

    return null;
  };

  private addRequiredValidator(fields: string[], index: number): void {
    fields.forEach((field: string) => {
      const formControl = this.getNestedFormControl(this.filters.at(index), field);
      if (formControl) {
        formControl.addValidators(Validators.required);
      }
    });
  }

  private removeRequiredValidator(fields: string[], index: number): void {
    fields.forEach((field: string) => {
      const formControl = this.getNestedFormControl(this.filters.at(index), field);
      if (formControl) {
        formControl.removeValidators(Validators.required);
      }
    });
  }

  private clearFilterFields(fields: string[], index: number): void {
    fields.forEach((field: string) => {
      const formControl = this.getNestedFormControl(this.filters.at(index), field);
      if (formControl) {
        formControl.setValue(null);
      }
    });
  }

  /**
   * Recursively navigates nested FormGroups based on a dot-separated path.
   */
  private getNestedFormControl(group: AbstractControl, fieldPath: string): AbstractControl | null {
    const pathParts = fieldPath.split('.');
    let control: AbstractControl | null = group;

    for (const part of pathParts) {
      if (control instanceof FormGroup) {
        control = control.get(part);
      } else {
        return null;
      }
    }

    return control;
  }

  private convertValueItem({ value, id, ...item }: HubspotEnrichmentFieldQueryFilterItem & { id: string }): HubspotEnrichmentFieldQueryFilterItem {
    return {
      ...item,
      ...this.convertValue({ value, ...item }),
      operatorDisplayName: item.operator ? OperatorDisplayName[item.operator] : null,
    };
  }

  private convertValue({ value, values, ...item }: HubspotEnrichmentFieldQueryFilterItem): Partial<HubspotEnrichmentFieldQueryFilterItem> {
    switch (item.dataType) {
      case FieldTypeEnum.DATE:
      case FieldTypeEnum.DATETIME:
        if (item.operator === OperatorEnum.BETWEEN) {
          return {
            value: null,
            values: null,
            range: {
              lowValue: item.range?.lowValue ? moment(item.range.lowValue).format('YYYY-MM-DD') : item.range.lowValue,
              highValue: item.range?.highValue ? moment(item.range.highValue).format('YYYY-MM-DD') : item.range.highValue,
            }
          }
        } else {
          return {
            value: value ? moment(value).format('YYYY-MM-DD') : value,
            values: null,
            range: {
              lowValue: null,
              highValue: null,
            }
          }
        }
      case FieldTypeEnum.NUMBER:
        if (item.operator === OperatorEnum.BETWEEN) {
          return {
            value: null,
            values: null,
            range: {
              lowValue: item.range?.lowValue,
              highValue: item.range?.highValue,
            }
          }
        } else {
          return {
            value,
            values: null,
            range: {
              lowValue: null,
              highValue: null,
            }
          }
        }
      case FieldTypeEnum.ENUM:
        return {
          values,
          value: null,
          range: {
            lowValue: null,
            highValue: null,
          }
        }
      default:
        return {
          value,
          values: null,
          range: {
            lowValue: null,
            highValue: null,
          }
        }
    }
  }

  private generateUniqueId(): string {
    return Math.random().toString(36).substr(2, 9); // Example: "abc123xyz"
  }
}
